import React, { createContext, useContext, useReducer } from 'react'
import cuid from 'cuid'

import {
	EArticleSectionTypes,
	EArticleActionTypes,
	IArticle,
	IArticleAction,
	IArticleState,
	EEntity,
} from 'models'

interface ArticleInterface {
	initialSection?: EArticleSectionTypes
	article?: IArticle
	reset?: boolean
}

let initialArticleState: IArticleState = {
	id: '',
	section: EArticleSectionTypes.post,
	author: undefined,
	title: '',
	body: '',
	postAs: EEntity.person,
	tags: [],
	tagging: false,
	image: null,
	featuredVideo: '',
	description: '',
	success: {
		publish: false,
	},
	error: {
		title: false,
		body: false,
		postAs: false,
		tags: false,
		featuredImage: false,
		featuredVideo: false,
		description: false,
	},
	isPost: undefined,
	createdAt: new Date(),
	updatedAt: new Date(),
	postingCompany: undefined,
}
const ArticleContext: React.Context<any> = createContext(null)
const reducer: React.Reducer<any, any> = (
	state: IArticleState,
	action: IArticleAction,
) => {
	let errorData: Object

	switch (action.type) {
		case EArticleActionTypes.resetState: {
			return { ...state, ...initialArticleState }
		}
		case EArticleActionTypes.setSection:
			return {
				...state,
				section:
					(action.section && EArticleSectionTypes[action.section]) ||
					initialArticleState.section,
			}
		case EArticleActionTypes.setTitle:
			return {
				...state,
				error: {
					...state.error,
					title: action.title ? action.title.trim().length < 1 : true,
				},
				title: action.title,
			}
		case EArticleActionTypes.seITags:
			return {
				...state,
				error: {
					...state.error,
					tags: false,
				},
				tags: action.tags,
				tagging: false,
			}
		case EArticleActionTypes.seITagsWithouITagging:
			errorData = state.error
			return {
				...state,
				error: {
					...state.error,
					...errorData,
					tags: false,
				},
				tags: action.tags,
			}
		case EArticleActionTypes.setBody:
			errorData = state.error
			return {
				...state,
				error: {
					...state.error,
					...errorData,
					body: false,
				},
				body: action.body,
			}
		case EArticleActionTypes.setPostAs:
			return {
				...state,
				error: {
					...state.error,
					postAs: false,
				},
				postAs: action.postAs,
				postAsId: action.postAsId,
			}
		case EArticleActionTypes.setDescription:
			return {
				...state,
				error: {
					...state.error,
					description: false,
				},
				description: action.description,
			}
		case EArticleActionTypes.setFeaturedImage:
			return {
				...state,
				error: {
					...state.error,
					featuredImage: false,
					featuredVideo: false,
				},
				featuredImage: action.featuredImage,
				featuredVideo: null,
			}
		case EArticleActionTypes.setFeaturedVideo:
			return {
				...state,
				error: {
					...state.error,
					featuredVideo: false,
					featuredImage: false,
				},
				image: null,
				featuredVideo: action.featuredVideo,
			}
		case EArticleActionTypes.clearMedia:
			return {
				...state,
				error: {
					...state.error,
					featuredVideo: false,
					featuredImage: false,
				},
				image: null,
				featuredVideo: null,
			}
		case EArticleActionTypes.seITagging:
			return {
				...state,
				tagging: action.tagging,
			}
		case EArticleActionTypes.setError:
			return {
				...state,
				error: {
					...state.error,
					...action.error,
				},
			}
		default:
			throw Error('Invalid Article action')
	}
}
export const ArticleProvider: React.FunctionComponent<ArticleInterface> = ({
	children,
	article,
	reset = false,
}) => {
	let state: IArticleState = { ...initialArticleState }
	if (!reset && article) {
		if (article.id) state.id = article.id
		state = {
			...state,

			title: article.title || '',
			body: article.body || '',
			postAs: article.postAs,
			postAsId:
				article.postAs === EEntity.company
					? (article.postingCompany && article.postingCompany) ??
					  undefined
					: undefined,
			tags: article.tags || [],
			description: article.description || '',
			tagging: false,
			image:
				(article.image && {
					id: article.image.id,
					filename: article.image.filename,
				}) ||
				null,
			featuredVideo: article.featuredVideo || null,
			author: article.author || undefined,
		}
		state.section = article.isPost
			? EArticleSectionTypes.post
			: EArticleSectionTypes.article
	}
	if (!state.id) {
		state.id = cuid()
	}

	return (
		<ArticleContext.Provider value={useReducer(reducer, state)}>
			{children}
		</ArticleContext.Provider>
	)
}
export const useArticleState = () => useContext(ArticleContext)
